import React, { useEffect, useRef, useState } from "react";
import { Route, Routes, useLocation } from "react-router";
import {
  authRoutes,
  publicRoutes,
  adminRoutes,
  agentRoutes,
} from "./router.link";
import Feature from "../feature";
import AuthFeature from "../authFeature";
import Login from "../auth/login";
import { Helmet } from "react-helmet-async";
import { all_routes } from "./all_routes";
import { fetchProfile } from "../../core/data/redux/slices/ProfileSlice";
import { fetchCredits } from "../../core/data/redux/slices/CreditsSlice";
import { useDispatch, useSelector } from "react-redux";
import PrivateRoute from "./PrivateRoute";
import AdminRoute from "./AdminRoute";
import AgentRoute from "./AgentRoute";
import { hideToast } from "../../core/data/redux/slices/ToastSlice";
import UserVerification from "../otherPages/UserVerification";
import CopyLinkPage from "../otherPages/CopyMagicLink";
import EmailChangeVerification from "../otherPages/EmailChangeVerification";
import AdminFeature from "../AdminFeature";
import AgentFeature from "../AgentFeature";
import LinkLogin from "../otherPages/LinkLogin";
import WabaMessageToast from "../../core/common/waba-message-toast/WabaMessageToast";
import PipedriveCallback from "../crm/settings/PipedriveCallback";

const ToastItem = ({ toast, dispatch }) => {
  const bsVariant = toast.variant === "error" ? "danger" : toast.variant;
  const [progress, setProgress] = useState(100);
  const pausedRef = useRef(false);
  const remainingRef = useRef(toast.delay);
  const lastTickRef = useRef(Date.now());
  const firedRef = useRef(false);

  useEffect(() => {
    lastTickRef.current = Date.now();
    const id = setInterval(() => {
      if (pausedRef.current) {
        lastTickRef.current = Date.now();
        return;
      }
      const now = Date.now();
      remainingRef.current = Math.max(0, remainingRef.current - (now - lastTickRef.current));
      lastTickRef.current = now;
      setProgress((remainingRef.current / toast.delay) * 100);
      if (remainingRef.current <= 0 && !firedRef.current) {
        firedRef.current = true;
        clearInterval(id);
        dispatch(hideToast(toast.id));
      }
    }, 50);
    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      className={`toast show align-items-center text-white bg-${bsVariant} border-0 mb-2`}
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      style={{ position: "relative", overflow: "hidden" }}
      onMouseEnter={() => { pausedRef.current = true; }}
      onMouseLeave={() => { lastTickRef.current = Date.now(); pausedRef.current = false; }}
    >
      <div className="toast-body d-flex justify-content-between align-items-start">
        <span>{toast.message}</span>
        <button type="button" className="btn-close btn-close-white ms-3" aria-label="Close" onClick={() => dispatch(hideToast(toast.id))} />
      </div>
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, background: "rgba(0,0,0,0.2)" }}>
        <div style={{ height: "100%", width: `${progress}%`, background: "rgba(255,255,255,0.75)", transition: "width 50ms linear" }} />
      </div>
    </div>
  );
};

const ALLRoutes = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { toasts } = useSelector((state) => state.toast);
  const userProfile = useSelector((state) => state.profile);
  const route = all_routes;

  // Find the current route in either public or auth routes
  const currentRoute =
    publicRoutes.find((route) => route.path === location.pathname) ||
    authRoutes.find((route) => route.path === location.pathname);

  // Construct the full title
  const fullTitle = currentRoute?.title
    ? `${currentRoute.title}`
    : "";

  useEffect(() => {
    document.title = fullTitle;
  }, [fullTitle]);
  useEffect(() => {
    document.documentElement.setAttribute("data-color", "blue");
  }, []);
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      dispatch(fetchProfile()).then(() => {
        dispatch(fetchCredits());
      });
    }
  }, [dispatch]);


  return (
    <>
      <Helmet>
        <title>{fullTitle}</title>
      </Helmet>
      <Routes>
        {/* Public Route - Login */}
        <Route path="/" element={<Login />} />
        <Route path="/user-verification" element={<UserVerification />} />
        <Route path="/copy-magic-link" element={<CopyLinkPage />} />
        <Route path="/link-login" element={<LinkLogin />} />
        <Route path="/pipedrive/callback" element={<PipedriveCallback />} />
        <Route
          path="/verify-email-change"
          element={<EmailChangeVerification />}
        />

        <Route element={<AuthFeature />}>
          {authRoutes.map((route, index) => (
            <Route path={route.path} element={route.element} key={index} />
          ))}
        </Route>

        {/* Super Admin Routes */}
        <Route
          element={
            <AdminRoute>
              <Routes>
                {adminRoutes.map((route, index) => {
                  // Skip routes with undefined paths
                  if (!route.path) return null;

                  return (
                    <Route element={<AdminFeature />} key={index}>
                      <Route path={route.path} element={route.element} />
                    </Route>
                  );
                })}
              </Routes>
            </AdminRoute>
          }
        >
          {adminRoutes.map((route, index) => {
            if (!route.path) return null;

            return (
              <Route element={<Feature />} key={index}>
                <Route path={route.path} element={route.element} />
              </Route>
            );
          })}
        </Route>

        {/* Agent Routes */}
        <Route
          element={
            <AgentRoute>
              <Routes>
                {agentRoutes.map((route, index) => {
                  // Skip routes with undefined paths
                  if (!route.path) return null;

                  return (
                    <Route element={<AgentFeature />} key={index}>
                      <Route path={route.path} element={route.element} />
                    </Route>
                  );
                })}
              </Routes>
            </AgentRoute>
          }
        >
          {agentRoutes.map((route, index) => {
            if (!route.path) return null;

            return (
              <Route element={<Feature />} key={index}>
                <Route path={route.path} element={route.element} />
              </Route>
            );
          })}
        </Route>

        {/* Company Admin Routes */}
        <Route
          path="/*"
          element={
            <PrivateRoute>
              <Routes>
                {publicRoutes.map((route, index) => {
                  // Skip routes with undefined paths
                  if (!route.path) return null;

                  return (  
                    <Route element={<Feature />} key={index}>
                      <Route path={route.path} element={route.element} />
                    </Route>
                  );
                })}
              </Routes>
            </PrivateRoute>
          }
        />
      </Routes>

      {/* Toast Notifications */}
      <div className="toast-container position-fixed top-0 end-0 p-3">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} dispatch={dispatch} />
        ))}
      </div>

      {/* WhatsApp Incoming Message Toast */}
      <WabaMessageToast />
    </>
  );
};

export default ALLRoutes;
